import { z } from "zod";
import type { AcCardStatus } from "./accreditation";

/**
 * The AI assistant (`POST /assistant/chat`, 2026-10-24): a chat that answers
 * from the caller's OWN view of the data. The model never sees SQL or a
 * table — it calls a few read-only tools, and each tool runs the existing
 * scoped reads with the caller's active role (`cardInScope` decides every
 * row), so a NOC gets its own organisation's numbers and nothing else.
 *
 * Stateless: the client keeps the conversation and sends it back (text only)
 * every turn. Links, guides and report downloads come back as ATTACHMENTS
 * built by the server from tool results — never URLs written by the model.
 *
 * Wire keys are snake_case; every string trims before it checks (bug 5).
 */

export const ASSISTANT_MESSAGE_MAX = 4000;
/** Turns the client may send back (user + assistant). Older turns are dropped by the client. */
export const ASSISTANT_HISTORY_MAX = 20;
/** Records a list attachment carries; the full list is one click away in GMS. */
export const ASSISTANT_RECORDS_LIMIT = 10;

/** Who may ask. The data each role then sees is its scope's, decided in SQL. */
export const ASSISTANT_REPORT_ROLES = ["SUPER_ADMIN"] as const;

/** The step-by-step guides the assistant can show (`get_guide`). */
export const ASSISTANT_GUIDE_TOPICS = [
	"register_record",
	"submit_record",
	"fix_returned_record",
	"upload_photo_and_id",
	"find_records",
	"review_records",
] as const;
export type AssistantGuideTopic = (typeof ASSISTANT_GUIDE_TOPICS)[number];

export const assistantChatBody = z.object({
	messages: z
		.array(
			z.object({
				role: z.enum(["user", "assistant"]),
				content: z.string().trim().min(1).max(ASSISTANT_MESSAGE_MAX),
			}),
		)
		.min(1)
		.max(ASSISTANT_HISTORY_MAX)
		.refine((m) => m[0]?.role === "user" && m[m.length - 1]?.role === "user", {
			message: "The conversation starts and ends with a user message",
		}),
});
export type AssistantChatBody = z.infer<typeof assistantChatBody>;
export type AssistantTurn = AssistantChatBody["messages"][number];

// ── Attachments ─────────────────────────────────────────────────────────────

/** A GMS path (`/accreditation/records/…`); the app prefixes its own GMS origin. */
export type GmsPath = string;

export interface AssistantRecordItem {
	ac_card_id: string;
	registration_no_display: string;
	name: string;
	organization: string;
	category: string;
	status: AcCardStatus;
	has_photo: boolean;
	path: GmsPath;
}

/** A WSL Notion page, board or card the answer points at. The app builds its own route (`/p/:id`, `/c/:board_id/:id`). */
export interface AssistantNotionItem {
	id: string;
	kind: "PAGE" | "BOARD" | "CARD";
	title: string;
	/** Where it sits: "A / B" for a page, the board and column for a card. */
	path: string;
	/** A card's board; null for a page. */
	board_id: string | null;
}

/** Labels are the app's (translated there); the server sends data only. Guide text is English (OVR / MSES / Notion are). */
export type AssistantAttachment =
	| { kind: "records"; total: number; items: AssistantRecordItem[]; path: GmsPath }
	| { kind: "records_link"; total: number; path: GmsPath }
	| { kind: "guide"; topic: AssistantGuideTopic; title: string; steps: Array<{ title: string; detail: string }>; path: GmsPath | null }
	| { kind: "report"; report_id: string; filename: string; record_count: number }
	| { kind: "notion"; items: AssistantNotionItem[] };

export interface AssistantChatResponse {
	reply: string;
	attachments: AssistantAttachment[];
}

/** `GET /assistant/reports/:id/url` — a fresh short download link for one of the caller's own reports. */
export interface AssistantReportUrl {
	url: string;
	expires_at: string;
}
